import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Play, Copy, Check, ChevronDown, Sparkles, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

const jsonPrompt = `I am uploading a lecture planner PDF. Convert the complete PDF into a clean, structured JSON file for my Tracker 360 study-planner app.

IMPORTANT REQUIREMENTS:

1. Read the ENTIRE PDF from the first page to the last page.

2. Do NOT skip any chapter or lecture.

3. Extract every chapter with its exact/proper chapter name as written in the PDF.

4. The JSON MUST include:

- subject
- batchName
- totalChapters
- totalLectures
- chapters
- lectureRecords

5. For every chapter, include:

- chapterNumber
- chapterId
- chapterName
- name
- displayName
- lectureCount
- lectures

6. For every lecture, include:

- lectureNumber
- subject
- chapterName
- chapter
- chapterNumber
- chapterId
- displayChapterName

7. VERY IMPORTANT:

The app must display the REAL chapter name, NOT generic names such as:

- Chapter 1
- Chapter 2
- Chapter 3

Example:

If the PDF says:

Chapter Name = "Trigonometric Functions"

Then every relevant lecture must contain:

"chapterName": "Trigonometric Functions"
"chapter": "Trigonometric Functions"
"displayChapterName": "Trigonometric Functions"

8. Lecture numbering must restart from 1 for each chapter according to the PDF.

Example:

Trigonometric Functions:
Lecture 1/8
Lecture 2/8
Lecture 3/8
...
Lecture 8/8

Then the next chapter should start again from:
Lecture 1/...

9. Preserve the chapter names and terminology from the PDF. Do not replace them with generic or invented names.

10. If text extraction from the PDF is unclear, inspect the PDF page visually/table image before deciding the chapter name or lecture information.

11. Make sure the final JSON contains EVERY chapter and EVERY lecture from the PDF.

12. Before creating the final file, verify:

- total chapter count
- total lecture count
- no missing chapters
- no missing lecture numbers
- every lecture has the correct chapterName
- no lecture displays "Chapter 1", "Chapter 2", etc. as its actual chapter name

Also add:

"schemaVersion": "tracker360-[subject]-v2"

and:

"displayRule": "Always display chapterName/displayChapterName; never generate Chapter 1, Chapter 2, etc."

FINAL OUTPUT:

Create and provide a downloadable .json file.

The JSON should be directly usable as a database/data source for Tracker 360.`;

const VIDEO_ID = 'vPFK3GzxSOU';

const GUIDE_STEPS = [
  {
    id: 'pdf',
    title: 'Get Your Lecture Planner PDF',
    explanation: 'Download the lecture planner PDF for the batch or coaching course you are following. This PDF should contain the chapters and lectures you want to track.',
    whatToDo: 'Download the planner PDF and keep it ready on your phone or computer.',
    next: 'Once you have the PDF, open ChatGPT.',
  },
  {
    id: 'upload-pdf',
    title: 'Upload the PDF to ChatGPT',
    explanation: 'Open ChatGPT and upload the lecture planner PDF you downloaded.',
    whatToDo: 'Attach the PDF in a ChatGPT chat. Make sure the complete planner is uploaded.',
    next: 'After uploading it, use the Tracker 360 JSON prompt.',
  },
  {
    id: 'prompt',
    title: 'Use the Tracker 360 JSON Prompt',
    explanation: 'Copy the Tracker 360 JSON prompt provided on this page and paste it into the same ChatGPT chat where you uploaded your PDF.',
    important: 'The prompt tells ChatGPT how to read your planner and create the structured JSON file required by Tracker 360.',
    next: 'Send the prompt and wait for ChatGPT to generate the JSON.',
  },
  {
    id: 'generate',
    title: 'Generate Your JSON File',
    explanation: 'ChatGPT will process your lecture planner and create structured JSON data containing the chapters and lectures.',
    whatToCheck: 'Make sure ChatGPT has completed the generation and that the JSON contains the required lecture information.',
    next: 'Download the generated JSON file.',
  },
  {
    id: 'download',
    title: 'Download the JSON File',
    explanation: 'Download the JSON file generated by ChatGPT to your device.',
    whatToDo: 'Save the file somewhere easy to find because you will upload it inside Tracker 360.',
    next: 'Open Tracker 360 Pro.',
  },
  {
    id: 'class',
    title: 'Choose Your Class or Batch',
    explanation: 'Inside Tracker 360 Pro, select the academic category that matches your preparation.',
    examples: 'Class 11th • Class 12th • Dropper Batch',
    next: 'After selecting your category, choose the subject you want to add.',
  },
  {
    id: 'subject',
    title: 'Choose Your Subject',
    explanation: 'Select the subject whose lecture planner you want to import.',
    mention: 'Physics, Chemistry, Mathematics, Biology, or any additional subject you have added.',
    next: 'Tracker 360 will show the upload area for that subject.',
  },
  {
    id: 'upload-json',
    title: 'Upload the Subject JSON',
    explanation: 'Upload the JSON file you generated for the selected subject.',
    whatToDo: 'Tap the upload button, choose the correct JSON file from your device, and wait for the file to load.',
    whatHappens: 'Tracker 360 reads the JSON and prepares the chapters and lectures automatically.',
    next: 'Repeat this for other subjects if needed.',
  },
  {
    id: 'add-more',
    title: 'Add More Subjects if Needed',
    explanation: 'Tracker 360 Pro is not limited to only the default subjects.',
    whatToDo: 'Use the Add Subject option to add another subject or subject type according to your preparation.',
  },
  {
    id: 'backlog',
    title: 'Select Your Backlog Chapters',
    explanation: 'Choose the chapters and lectures you still need to complete.',
    whatToDo: 'Tick the chapters you want Tracker 360 to include in your roadmap.',
    whatHappens: 'Tracker 360 uses your selected backlog to calculate your study workload.',
    next: 'Continue to configure your target timeline.',
  },
  {
    id: 'timeline',
    title: 'Set Your Target Timeline',
    explanation: 'Choose how many days you want to take to complete your selected backlog.',
    whatToExpect: 'Tracker 360 automatically calculates the daily lecture pace required to reach your target.',
    also: 'You can adjust the lecture playback speed and choose the date from which your roadmap should start.',
    next: 'Generate your day-by-day timetable.',
  },
  {
    id: 'roadmap',
    title: 'Generate Your Study Roadmap',
    explanation: 'Tracker 360 now creates your day-by-day study plan using your selected subjects, backlog, timeline and lecture pace.',
    whatToExpect: 'What you need to do each day is organised into your roadmap.',
    next: 'Follow the plan and mark your lectures as you complete them.',
  },
  {
    id: 'track',
    title: 'Track Your Progress',
    explanation: 'Once your roadmap is created, use Tracker 360 to track your preparation over time.',
    whatToDo: 'Mark lectures as completed, follow your planned workload and monitor your preparation progress.',
  },
];

function StepRow({ step, index, isOpen, onToggle }) {
  return (
    <div
      className={cn(
        'rounded-2xl border transition-colors duration-300 overflow-hidden',
        isOpen
          ? 'bg-white/80 dark:bg-[var(--background)]/80 border-[rgba(130,150,125,0.25)] shadow-[0_0_40px_-20px_rgba(111,174,53,0.18)]'
          : 'bg-white/60 dark:bg-[var(--background)]/40 border-[rgba(130,150,125,0.16)] hover:border-[rgba(130,150,125,0.25)]'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
      >
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold transition-colors',
            isOpen
              ? 'bg-lime-400 text-[#0a1206] shadow-[0_0_16px_-4px_rgba(154,205,75,0.7)]'
              : 'bg-[var(--background)]/50 dark:bg-white/5 text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)] border border-[rgba(130,150,125,0.16)]'
          )}
        >
          {index + 1}
        </span>
        <span className="flex-1 text-sm font-semibold text-[var(--foreground)]">
          {step.title}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 shrink-0 text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)] transition-transform duration-300',
            isOpen && 'rotate-180 text-lime-500'
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="px-4 pb-4 pt-0 space-y-3">
              <p className="text-[13px] leading-relaxed text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
                {step.explanation}
              </p>

              {step.whatToDo && (
                <div className="rounded-xl bg-[var(--background)]/60 dark:bg-white/[0.03] border border-[rgba(130,150,125,0.12)] p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)] dark:text-lime-400 mb-1">
                    What to do
                  </p>
                  <p className="text-[13px] text-[var(--foreground)]">{step.whatToDo}</p>
                </div>
              )}

              {step.whatToCheck && (
                <div className="rounded-xl bg-[var(--background)]/60 dark:bg-white/[0.03] border border-[rgba(130,150,125,0.12)] p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)] dark:text-lime-400 mb-1">
                    What to check
                  </p>
                  <p className="text-[13px] text-[var(--foreground)]">{step.whatToCheck}</p>
                </div>
              )}

              {step.important && (
                <div className="rounded-xl bg-lime-400/10 dark:bg-lime-400/[0.08] border border-lime-400/20 p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)] dark:text-lime-400 mb-1">
                    Important
                  </p>
                  <p className="text-[13px] text-[var(--foreground)]">{step.important}</p>
                </div>
              )}

              {step.examples && (
                <div className="rounded-xl bg-[var(--background)]/60 dark:bg-white/[0.03] border border-[rgba(130,150,125,0.12)] p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)] dark:text-lime-400 mb-1">
                    Examples
                  </p>
                  <p className="text-[13px] text-[var(--foreground)]">{step.examples}</p>
                </div>
              )}

              {step.mention && (
                <div className="rounded-xl bg-[var(--background)]/60 dark:bg-white/[0.03] border border-[rgba(130,150,125,0.12)] p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)] dark:text-lime-400 mb-1">
                    Includes
                  </p>
                  <p className="text-[13px] text-[var(--foreground)]">{step.mention}</p>
                </div>
              )}

              {step.whatHappens && (
                <div className="rounded-xl bg-[var(--background)]/60 dark:bg-white/[0.03] border border-[rgba(130,150,125,0.12)] p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)] dark:text-lime-400 mb-1">
                    What happens
                  </p>
                  <p className="text-[13px] text-[var(--foreground)]">{step.whatHappens}</p>
                </div>
              )}

              {step.whatToExpect && (
                <div className="rounded-xl bg-[var(--background)]/60 dark:bg-white/[0.03] border border-[rgba(130,150,125,0.12)] p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)] dark:text-lime-400 mb-1">
                    What to expect
                  </p>
                  <p className="text-[13px] text-[var(--foreground)]">{step.whatToExpect}</p>
                </div>
              )}

              {step.also && (
                <p className="text-[13px] leading-relaxed text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">{step.also}</p>
              )}

              {step.next && (
                <div className="flex items-center gap-2 text-[13px] font-medium text-[var(--primary)] dark:text-lime-400">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime-500" />
                  Next: {step.next}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function TrackerTutorialGuide() {
  const [copied, setCopied] = useState(false);
  const [openStep, setOpenStep] = useState('pdf');

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const toggleStep = (id) => {
    setOpenStep((current) => (current === id ? null : id));
  };

  return (
    <div className="w-full space-y-5 mt-6">
      {/* Section Header */}
      <div className="space-y-1">
        <h3 className="text-lg font-bold tracking-tight text-[var(--foreground)]">
          How to Use Tracker 360 Pro
        </h3>
        <p className="text-[13px] text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
          Follow these simple steps to turn your lecture planner PDF into a working study tracker.
        </p>
      </div>

      {/* Video Tutorial Card */}
      <div className="p-5 rounded-2xl bg-white/70 dark:bg-[var(--background)]/60 backdrop-blur-xl border border-[rgba(130,150,125,0.16)] shadow-sm space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="p-2.5 rounded-xl bg-gradient-to-tr from-lime-400 to-emerald-500 text-[#0a1206] shadow-[0_8px_24px_-10px_rgba(111,174,53,0.5)]">
              <Play className="w-4 h-4 fill-current" />
            </span>
            <div>
              <h4 className="text-sm font-bold text-[var(--foreground)]">
                Watch the Video Guide
              </h4>
              <p className="text-xs text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
                Complete walkthrough: PDF → JSON → Tracker 360
              </p>
            </div>
          </div>
          <a
            href={`https://youtu.be/${VIDEO_ID}`}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[var(--background)] dark:bg-white/5 text-xs font-semibold text-[var(--primary)] dark:text-lime-400 border border-[rgba(130,150,125,0.16)] hover:border-lime-400/40 transition"
          >
            Watch on YouTube <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-[rgba(130,150,125,0.16)] bg-black">
          <iframe
            className="w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}`}
            title="How To Use Tracker 360 And How To Convert Any PDF File Into JSON"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>

      {/* ChatGPT Prompt Card */}
      <div className="p-5 rounded-2xl bg-gradient-to-r from-lime-400/10 via-emerald-400/5 to-transparent dark:from-lime-400/[0.08] dark:via-emerald-400/[0.04] border border-lime-400/20 dark:border-lime-400/20 backdrop-blur-xl space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-5 h-5 text-lime-600 dark:text-lime-400" />
            <div>
              <h4 className="text-sm font-bold text-[var(--foreground)]">
                ChatGPT PDF-to-JSON Prompt
              </h4>
              <p className="text-xs text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
                Copy this prompt and send it with your PDF in ChatGPT.
              </p>
            </div>
          </div>
          <button
            onClick={copyToClipboard}
            className={cn(
              'px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-md',
              copied
                ? 'bg-emerald-600 text-white shadow-emerald-500/25'
                : 'bg-gradient-to-r from-lime-400 to-emerald-400 text-[#0a1206] shadow-lime-500/25 hover:opacity-95'
            )}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" /> Prompt Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" /> Copy JSON Prompt
              </>
            )}
          </button>
        </div>
        <div className="relative p-3.5 rounded-xl bg-white/80 dark:bg-[var(--background)]/80 border border-[rgba(130,150,125,0.16)] text-[11px] font-mono text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)] max-h-28 overflow-y-auto custom-scroll">
          {jsonPrompt}
        </div>
      </div>

      {/* Expandable Step Guide */}
      <div className="space-y-3">
        {GUIDE_STEPS.map((step, idx) => (
          <StepRow
            key={step.id}
            step={step}
            index={idx}
            isOpen={openStep === step.id}
            onToggle={() => toggleStep(step.id)}
          />
        ))}
      </div>
    </div>
  );
}
